'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

const CLICK_LOCK_MS = 800;

export const getScrollParent = (el: HTMLElement): HTMLElement | Window => {
  let node: HTMLElement | null = el.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node;
    }
    node = node.parentElement;
  }
  return window;
};

// Scrolls the section identified by `section-${sectionId}` into view, picking
// the right scroll container automatically — the window in page mode, or the
// AsideModal's inner scroller in modal mode (where body scroll is locked).
export const scrollToSection = (sectionId: string, offsetPx: number) => {
  const section = document.getElementById(`section-${sectionId}`);
  if (!section) return;

  const scrollParent = getScrollParent(section);

  if (scrollParent === window) {
    const top =
      section.getBoundingClientRect().top + window.scrollY - offsetPx;
    window.scrollTo({ top, behavior: 'smooth' });
    return;
  }

  const parent = scrollParent as HTMLElement;
  const top =
    section.getBoundingClientRect().top -
    parent.getBoundingClientRect().top +
    parent.scrollTop -
    offsetPx;
  parent.scrollTo({ top, behavior: 'smooth' });
};

// Tracks which ProductDetails section is currently visible under the sticky
// nav and exposes a setter that suppresses scroll-driven updates while a
// programmatic smooth scroll is in flight (e.g. after clicking a nav item).
export const useActiveSection = (
  sectionIds: string[],
  offsetPx: number,
): readonly [string, (id: string) => void] => {
  const [activeId, setActiveId] = useState<string>(sectionIds[0] ?? '');
  const ignoreUntilRef = useRef(0);
  const sectionIdsKey = sectionIds.join('|');

  useEffect(() => {
    const ids = sectionIdsKey.split('|').filter(Boolean);
    if (ids.length === 0) return;

    const entries = ids
      .map((id) => ({ id, el: document.getElementById(`section-${id}`) }))
      .filter((x): x is { id: string; el: HTMLElement } => x.el != null);

    if (entries.length === 0) return;

    const scrollParent = getScrollParent(entries[0].el);

    const compute = () => {
      if (Date.now() < ignoreUntilRef.current) return;

      const reference =
        scrollParent === window
          ? offsetPx + 1
          : (scrollParent as HTMLElement).getBoundingClientRect().top +
            offsetPx +
            1;

      let current = entries[0].id;
      for (const { id, el } of entries) {
        if (el.getBoundingClientRect().top <= reference) {
          current = id;
        } else {
          break;
        }
      }
      setActiveId((prev) => (prev === current ? prev : current));
    };

    compute();

    const opts: AddEventListenerOptions = { passive: true };
    scrollParent.addEventListener('scroll', compute, opts);
    window.addEventListener('resize', compute, opts);

    return () => {
      scrollParent.removeEventListener('scroll', compute);
      window.removeEventListener('resize', compute);
    };
  }, [sectionIdsKey, offsetPx]);

  const setActiveManually = useCallback((id: string) => {
    setActiveId(id);
    // Suppress scroll-driven updates while the smooth scroll animation runs,
    // otherwise intermediate sections would briefly flash as active.
    ignoreUntilRef.current = Date.now() + CLICK_LOCK_MS;
  }, []);

  return [activeId, setActiveManually] as const;
};
